/**
 * Asymmetric crypto for secure sharing.
 *
 * Each user has an RSA-OAEP (2048-bit, SHA-256) keypair. The public key is
 * stored in plaintext (it is public); the private key is encrypted with the
 * user's symKey. To share an item, the sender wraps the item's symmetric key
 * with the recipient's public key — the server stores only that ciphertext.
 */

const RSA_PARAMS = { name: 'RSA-OAEP', modulusLength: 2048, publicExponent: new Uint8Array([1, 0, 1]), hash: 'SHA-256' } as const;

import type { Bytes } from './encoding.js';

/** Generate a fresh RSA-OAEP keypair. */
export async function generateKeyPair(): Promise<CryptoKeyPair> {
  return (await crypto.subtle.generateKey(RSA_PARAMS, true, ['encrypt', 'decrypt'])) as CryptoKeyPair;
}

/** Export a public key as SPKI base64url (safe to store on the server). */
export async function exportPublicKey(key: CryptoKey): Promise<string> {
  const spki = await crypto.subtle.exportKey('spki', key);
  return bufferToB64url(spki as ArrayBuffer);
}

/** Export a private key as PKCS#8 bytes (caller encrypts these before storing). */
export async function exportPrivateKeyBytes(key: CryptoKey): Promise<Bytes> {
  const pkcs8 = await crypto.subtle.exportKey('pkcs8', key);
  return new Uint8Array(pkcs8 as ArrayBuffer);
}

/** Import a public key from SPKI base64url. */
export async function importPublicKey(spkiB64: string): Promise<CryptoKey> {
  return crypto.subtle.importKey('spki', b64urlToBuffer(spkiB64), RSA_IMPORT, true, ['encrypt']);
}

/** Import a private key from PKCS#8 bytes (after the caller has decrypted them). */
export async function importPrivateKey(pkcs8: Bytes): Promise<CryptoKey> {
  return crypto.subtle.importKey('pkcs8', pkcs8, RSA_IMPORT, false, ['decrypt']);
}

// Typed as a const object (not annotated) so it's assignable to importKey's
// algorithm param across DOM and Workers type libs without excess-property errors.
const RSA_IMPORT = { name: 'RSA-OAEP', hash: 'SHA-256' } as const;

/**
 * Wrap an item key for a recipient using their public key.
 * RSA-OAEP-2048 can encrypt up to 190 bytes; an item key is 32 bytes.
 */
export async function wrapItemKeyForRecipient(recipientPublicKey: CryptoKey, itemKey: Bytes): Promise<Bytes> {
  const shared = await crypto.subtle.encrypt({ name: 'RSA-OAEP' }, recipientPublicKey, itemKey);
  return new Uint8Array(shared);
}

/** Unwrap an item key using the recipient's private key. */
export async function unwrapItemKeyWithPrivateKey(privateKey: CryptoKey, shared: Bytes): Promise<Bytes> {
  const itemKey = await crypto.subtle.decrypt({ name: 'RSA-OAEP' }, privateKey, shared);
  return new Uint8Array(itemKey);
}

// --- tiny base64url helpers local to this module to avoid an import cycle through encoding.js public surface ---
function bufferToB64url(buf: ArrayBuffer | Bytes): string {
  const bytes = buf instanceof Uint8Array ? buf : new Uint8Array(buf);
  let s = '';
  for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i]!);
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function b64urlToBuffer(b64: string): Bytes {
  const normalized = b64.replace(/-/g, '+').replace(/_/g, '/');
  const padded = normalized.padEnd(normalized.length + ((4 - (normalized.length % 4)) % 4), '=');
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}
